import React, { useCallback, useState, useEffect, useRef } from 'react'
import { Link } from 'gatsby'
import { gsap, useScrollSmoother } from '@dboxdev/ui-components/gsap'
import { useForm } from 'react-hook-form'
import Form from 'components/ui-elements/form'
import TextInput from 'components/ui-elements/form/text-input'
import SelectInput from 'components/ui-elements/form/select'
import CheckBoxInput from 'components/ui-elements/form/checkbox-input'
import Button from 'components/ui-elements/button'
import {
  Wrapper,
  FormWrapper,
  Title,
  SubTitle,
  Description,
  FormContent,
  InputsWrapper,
  Row,
  ConsentWrapper,
  Disclaimer,
  SubmitError,
  ButtonWrapper,
  ThanksMessage,
  ThanksMessageTitle,
  ThanksMessageDescription
} from './index.styled'
import { StaticImage } from 'gatsby-plugin-image'

const Inquire = ({ placement = '', formname = '' }) => {
  // onTouched validates on first blur, then live as the field is corrected, so
  // nothing is flagged while the visitor is still typing.
  const form = useForm({ mode: 'onTouched' })
  const smoother = useScrollSmoother()
  const wrapperRef = useRef(null)
  const formRef = useRef(null)
  const [submitting, setSubmitting] = useState(false)
  const [isSubmit, setIsSubmit] = useState(false)
  const [submitError, setSubmitError] = useState('')

  const selectContacted = [
    { value: 'No preference', label: 'No Preference' },
    { value: 'Call', label: 'Phone Call' },
    { value: 'Text', label: 'Text - requires consent below' },
    { value: 'Email', label: 'Email' }
  ]

  const getCookie = (name) => {
    if (typeof document === 'undefined') return ''
    const match = document.cookie.match(new RegExp('(^| )' + name + '=([^;]+)'))
    return match ? decodeURIComponent(match[2]) : ''
  }

  useEffect(() => {
    if (typeof window === 'undefined') return

    const urlSearchParams = new URLSearchParams(window.location.search)

    const utmSource = getCookie('utm_source')
    const utmMedium = getCookie('utm_medium')
    let sourceMedium = `${utmSource || ''} ${utmMedium || ''}`.trim()

    if (!sourceMedium) {
      sourceMedium = urlSearchParams.get('gclid') ? 'Adwords' : 'Website - 4S Landing'
    }

    form.setValue('SourceMedium', sourceMedium)
    form.setValue('gclid', urlSearchParams.get('gclid') || getCookie('gclid') || '')
    // Named for the HubSpot property, not the prop: unlabelled fields fall
    // through to their name attribute in HubSpot's collected-forms capture, so
    // matching the internal property name lets the capture map it directly.
    form.setValue('form_placement', placement)
    form.setValue('formname', formname)

    const tracked = (name) => urlSearchParams.get(name) || getCookie(name) || ''

    form.setValue('utm_source', tracked('utm_source'))
    form.setValue('utm_medium', tracked('utm_medium'))
    form.setValue('utm_campaign', tracked('utm_campaign'))
    form.setValue('utm_term', tracked('utm_term'))
    form.setValue('utm_content', tracked('utm_content'))
    form.setValue('AmenBookCode', tracked('AmenBookCode'))
  }, [form, placement, formname])

  // Drop the message as soon as the form is complete, so it does not linger
  // while the visitor scrolls back down to the button.
  useEffect(() => {
    if (form.formState.isValid) setSubmitError('')
  }, [form.formState.isValid])

  const onSubmit = useCallback((input) => {
    setSubmitting(true)
    setSubmitError('')
    fetch('/form-submission', {
      method: 'POST',
      mode: 'cors',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(input)
    })
      .then((response) => {
        // fetch only rejects on network failure, so a 4xx/5xx would otherwise
        // fall through to the thank-you message and the lead would be lost.
        if (!response.ok) throw new Error(`Form submission failed: ${response.status}`)

        setIsSubmit(true)
        if (smoother.current && wrapperRef.current) {
          gsap.to(smoother.current, {
            scrollTop: smoother.current.offset(wrapperRef.current),
            duration: 1
          })
        }
        if (window.dataLayer) {
          window.dataLayer.push({ event: 'form_submit' })
        }
        if (typeof window !== 'undefined' && typeof window.gtag_report_conversion === 'function') {
          window.gtag_report_conversion()
        }

        const xadPixel = new window.Image()
        xadPixel.src = `https://bidagent.xad.com/conv/239171?ts=${Date.now()}`
      })
      .catch((error) => {
        console.error(error.message)
        setSubmitting(false)
        setSubmitError('Something went wrong sending your inquiry. Please try again.')
      })
  }, [smoother])

  // The submit control is a plain button rather than type='submit', so an
  // invalid form never fires a DOM submit event at all. HubSpot's tracking
  // script listens for that event and would otherwise record abandoned or
  // rejected attempts as contacts in the CRM.
  //
  // trigger() validates every field and surfaces all errors at once, which also
  // covers fields the browser or a password manager autofilled — those are
  // never focused, so blur validation alone would leave them silently invalid.
  // handleSubmit rather than trigger, because it also marks the form as
  // submitted — from then on reValidateMode ('onChange' by default) revalidates
  // every field as it is edited, including ones never blurred. trigger() alone
  // shows the errors but leaves those fields waiting for a blur to clear.
  const handleSubmitClick = useCallback(() => form.handleSubmit(
    () => {
      setSubmitError('')
      if (!formRef.current) return

      // A real submit event, so HubSpot still captures genuine submissions.
      if (typeof formRef.current.requestSubmit === 'function') {
        formRef.current.requestSubmit()
      } else {
        formRef.current.dispatchEvent(new Event('submit', { bubbles: true, cancelable: true }))
      }
    },
    () => setSubmitError('Please fill in all required fields.')
  )(), [form])

  return (
    <Wrapper ref={wrapperRef}>
      <FormWrapper className={isSubmit ? 'hide' : 'show'}>
        <Title className='fadeInFromBottom'>REGISTER<br className='for-mobile' /> YOUR INTEREST</Title>
        <SubTitle className='fadeInFromBottom'>elevated living on lake austin<br /> STARTING AT $4.5 Million</SubTitle>
        <Description className='fadeInFromBottom'>To receive more information about the project including pricing, availability and a PDF of the 160 page book that describes the project in more detail, please provide the following:</Description>
        {/*
          These two match the Webflow form exactly, so HubSpot's collected-forms
          capture files submissions against the same form record and the existing
          reporting keeps counting. Its scrape reads id and class as separate
          identifying fields (HubSpot's UI just shows them joined as
          "#wf-form-LakeAustinFormHiddenP1, .form"), so both have to match.

          Do not add a styling class here. The capture builds the class half from
          the whole className, space-split — one extra class turns it into
          ".form, .something-else", stops matching, and silently sends every
          submission to a new unnamed form record with no error anywhere.
        */}
        <Form
          id='wf-form-LakeAustinFormHiddenP1'
          className='form'
          onSubmit={onSubmit}
          formProps={form}
          formRef={formRef}
        >
          <FormContent className='fadeInFromBottom'>
            <InputsWrapper>
              <Row>
                <TextInput
                  name='firstName'
                  title='First Name'
                  required
                  rules={{
                    required: true,
                    pattern: /^[a-zA-Z]+(?:-[a-zA-Z]+)?(?: [a-zA-Z]+)?$/
                  }}
                  maxLength={255}
                  onBlur={(e) => {
                    e.target.value = e.target.value.trim()
                    form.setValue('firstName', e.target.value)
                  }}
                />
              </Row>
              <Row>
                <TextInput
                  name='lastName'
                  title='Last Name'
                  required
                  rules={{
                    required: true,
                    pattern: /^[a-zA-Z]+(?:-[a-zA-Z]+)?(?: [a-zA-Z]+)?$/
                  }}
                  maxLength={255}
                  onBlur={(e) => {
                    e.target.value = e.target.value.trim()
                    form.setValue('lastName', e.target.value)
                  }}
                />
              </Row>
              <Row>
                <TextInput
                  name='email'
                  title='Email Address'
                  type='email'
                  required
                  rules={{
                    required: true,
                    pattern: /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/
                  }}
                  onBlur={(e) => {
                    e.target.value = e.target.value.trim()
                    form.setValue('email', e.target.value)
                  }}
                />
              </Row>
              <Row>
                <TextInput
                  name='phone'
                  title='Phone Number'
                  type='tel'
                  maxLength={18}
                />
              </Row>
              <Row>
                <TextInput
                  name='zip'
                  title='Zip Code'
                  maxLength={18}
                />
              </Row>
            </InputsWrapper>
            <Row>
              <SelectInput
                name='contactType'
                title='How would you like to be contacted?'
                defaultValue='No preference'
                options={selectContacted}
              />
            </Row>
            <Row>
              <TextInput
                name='moreInfo'
                title='Tell us more about your needs (size of residence, preferred views, etc)'
                type='textarea'
                maxLength={5000}
              />
            </Row>
            <Row>
              <TextInput
                name='social'
                title='Tell us more about you'
                type='textarea'
                maxLength={5000}
              />
            </Row>
            <Row>
              <TextInput
                name='hearAbout'
                title='How did you hear about us?'
                type='textarea'
                maxLength={5000}
              />
            </Row>
            <Row style={{ display: 'none' }}>
              <TextInput name='SourceMedium' title='' />
              <TextInput name='gclid' title='' />
              <TextInput name='form_placement' title='' />
              <TextInput name='formname' title='' />
              <TextInput name='utm_source' title='' />
              <TextInput name='utm_medium' title='' />
              <TextInput name='utm_campaign' title='' />
              <TextInput name='utm_term' title='' />
              <TextInput name='utm_content' title='' />
              <TextInput name='AmenBookCode' title='' />
            </Row>
            <ConsentWrapper>
              <CheckBoxInput
                name='smsTransactionalConsent'
                defaultValue={false}
                title='I consent to receive transactional text messages such as information regarding my inquiry from Four Seasons Private Residences Lake Austin at the provided number.'
              />
              <CheckBoxInput
                name='smsMarketingConsent'
                defaultValue={false}
                title='I consent to receive marketing text messages from Four Seasons Private Residences Lake Austin at the provided number.'
              />
            </ConsentWrapper>
            <Disclaimer>
              Consent is not a condition of purchase. Msg frequency varies.<br className='for-mobile' /> Msg &amp; data rates may apply. Reply STOP to opt out, HELP for help.<br className='for-mobile' /> View our <Link to='/privacy'>Privacy Policy</Link> and <Link to='/privacy#legal'>Legal Disclosure</Link>.
            </Disclaimer>
            {submitError && <SubmitError>{submitError}</SubmitError>}
            <ButtonWrapper>
              <Button as='button' type='button' disabled={submitting} onClick={handleSubmitClick}>
                {submitting ? 'Sending...' : 'Submit'}
              </Button>
            </ButtonWrapper>
          </FormContent>
        </Form>
      </FormWrapper>
      <ThanksMessage className={isSubmit ? 'show' : ''}>
        <ThanksMessageTitle>THANK YOU FOR<br className='for-mobile' /> YOUR INTEREST</ThanksMessageTitle>
        <ThanksMessageDescription>
          We look forward to sharing updates about<br className='for-mobile' /> Four Seasons Private Residences Lake Austin.<br /><br className='for-mobile' /> In the meantime, we invite you to download the PDF of<br className='for-mobile' /> the 160 page book<br className='for-desktop' /> that describes the project in more detail.
        </ThanksMessageDescription>
        <Button as='a' href='https://bit.ly/lakeaustin2023PDF' target='_blank'>DOWNLOAD</Button>
        <StaticImage src='../../images/home/book.jpg' alt='Four Seasons Private Residences Lake Austin Book' />
      </ThanksMessage>
    </Wrapper>
  )
}

export default Inquire
